import type { ForwardRefExoticComponent, SyntheticEvent } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { observer } from 'mobx-react';
import { Link } from '@compat/router';
import icons from '@icons';
import { Picture } from '@ui';
import useWindowParamsOnScroll from '@customHooks/useWindowParamsOnScroll';
import CatalogBtnList from '../CatalogBtnList';
import type { CatalogTypesModel } from '@schema/cart';
import { useScrollbarWidth } from '@customHooks/useWindowScrollbarWidth';
import { lockBodyScroll, unlockBodyScroll } from '@utils/scrollLock';
import './index.scss';
import type { CatalogMenuItem } from '@store/CatalogStore.type';
import { useStores } from '@root/src/stores';

const {
  menu: { white: menuWhiteOutlined },
  ng: { white: ngWhiteOutlined }
} = icons.Outline;

const isNewYear = false;

const icon = isNewYear ? ngWhiteOutlined : menuWhiteOutlined;

interface CatalogBtnDesktopProps {
  isShow: boolean;
  setIsShow?: (value: boolean) => void;
}

export type CatalogPromoMenuItem = Pick<
  CatalogTypesModel,
  'id' | 'title' | 'shortTitle' | 'short_title' | 'identify'
>;
let isInitialMount = true;
const CatalogBtnDesktop = observer(
  forwardRef<any, CatalogBtnDesktopProps>(({ isShow, setIsShow }, ref) => {
    const { catalogStore } = useStores();
    const { specialMenu, menu } = catalogStore;
    const [activeMenu, setActiveMenu] = useState<CatalogMenuItem | null>(null);
    const windowScrollbarWidth = useScrollbarWidth();
    const [headerCatalogWrapper, setHeaderCatalogWrapper] =
      useState<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!isInitialMount && catalogStore.menu?.length) {
        const activeMenu = menu.find(item => item.isActive) || menu[0];
        setActiveMenu(activeMenu);
      }
      isInitialMount = false;
    }, [catalogStore.menu?.length, menu]);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      const headerMainEl: HTMLElement | null =
        document.querySelector('.header-main__main');
      if (isShow) {
        if (headerMainEl) {
          headerMainEl.classList.add('header-main__main--show');
          const { width, left } = headerMainEl.getBoundingClientRect();
          if (headerCatalogWrapper) {
            headerCatalogWrapper.style.width = `${width}px`;
            headerCatalogWrapper.style.left = `${left}px`;
          }
        }
        lockBodyScroll();
        document.body.style.paddingRight = `${windowScrollbarWidth}px`;
      } else {
        headerMainEl?.classList?.remove('header-main__main--show');
      }
      return () => {
        headerMainEl?.classList?.remove('header-main__main--show');
        if (isShow) {
          unlockBodyScroll();
          document.body.style.removeProperty('padding-right');
        }
      };
    }, [isShow, headerCatalogWrapper, windowScrollbarWidth]);

    const isInfoHeaderHidden = useWindowParamsOnScroll(18);

    const show = useCallback((): void => {
      timerRef.current = setTimeout(() => {
        setIsShow?.(true);
      }, 300);
    }, [setIsShow]);

    const hide = useCallback((): void => {
      clearTimeout(timerRef.current as ReturnType<typeof setTimeout>);
      // Body scroll / padding-right are released by the isShow effect cleanup.
      setIsShow?.(false);
    }, [setIsShow]);

    useImperativeHandle(
      ref,
      () => ({
        hide
      }),
      [hide]
    );

    const onCatalogItemClick = useCallback(
      (e: SyntheticEvent): void => {
        e.stopPropagation();
        hide();
      },
      [hide]
    );

    const setRef = useCallback((node: HTMLDivElement) => {
      setHeaderCatalogWrapper(node);
    }, []);

    return (
      <div
        onMouseEnter={show}
        onMouseLeave={hide}
        className={`header-catalog${isShow ? ' header-catalog--visible' : ''}`}
      >
        <Link onClick={hide} to='/catalog'>
          <button className='catalog-btn'>
            Каталог
            <Picture
              pictureClassName='catalog-btn__icon'
              icon={icon}
              height='24'
              width='24'
            />
          </button>
        </Link>
        {isShow && activeMenu && (
          <div
            ref={setRef}
            style={{
              maxWidth: '1440px',
              borderRadius: '0 0 8px 8px',
              padding: '0 20px'
            }}
            className={`header-catalog__wrapper${
              isInfoHeaderHidden ? ' header-catalog__wrapper--scrolled' : ''
            }`}
          >
            <CatalogBtnList
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              catalogSpecialMenu={specialMenu}
              catalogList={menu}
              onCatalogItemClick={onCatalogItemClick}
              hide={hide}
            />
          </div>
        )}
      </div>
    );
  })
);

export default CatalogBtnDesktop as ForwardRefExoticComponent<
  CatalogBtnDesktopProps & React.RefAttributes<any>
>;
